import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useToast } from "@chakra-ui/react";
import fileinfoService from "api/common/fileinfo.service";
import { foundationV2Keys, issbKeys, useSaveAnswer } from "hooks/query";
import { showErrorToast } from "utils";
import { normalizeFileInfoList, preferIdsFromQuestionFiles } from "utils/fileinfoIds";

const idsOf = (list) => (list || []).map((f) => String(f.id));

/**
 * The year's sources and its Additional Information, and the single Save that writes both.
 *
 * Both live on the question — `issb_question.files` and `issb_question.answer` — and go through the existing
 * `POST /api/issb/answer`. There is no free-text column on `foundation_data_year` and none is needed.
 *
 * **They must always be sent together.** `IssbAnswerFacade.saveAnswer` sets both unconditionally with no null
 * guard, and `IssbAnswerDto` is `@JsonInclude(NON_EMPTY)` — so a save that omits `files` nulls the file list,
 * and one that omits `answer` wipes the notes.
 *
 * @param {object|null} question
 * @param {string} questionId
 * @param {Array} runs this workstream's extraction runs, used to recover sources predating this feature
 */
export default function useYearAnswerSave(question, questionId, runs) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [sources, setSources] = useState([]);
  const [answerDraft, setAnswerDraft] = useState(null);
  /*
    What was on screen when this question loaded — not what was persisted. Years predating this feature have
    runs but an empty `files`, so the list is seeded from both (below); baselining against the persisted set
    instead would load every such year already dirty, with Submit blocked by a change nobody made.
  */
  const baselineRef = useRef([]);
  const seededForRef = useRef(null);

  /*
    Derived from a joined KEY rather than from `question` so the effect's dependency is a primitive. A
    `question?.files || []` fallback would be a new array every render, which fails
    react-hooks/exhaustive-deps and therefore CI.
  */
  const persistedKey = useMemo(
    () => preferIdsFromQuestionFiles(question?.files).join(","),
    [question]
  );
  /*
    What the SERVER holds, as opposed to what is on screen. Exposed because a source that is not persisted yet
    must not offer to be extracted — a run would then reference a file the question does not list. Derived from
    the key string so it is a stable reference, and it updates on its own: a save invalidates the answer query,
    the question refetches, and `files` catches up.
  */
  const persistedSourceIds = useMemo(
    () => (persistedKey ? persistedKey.split(",") : []),
    [persistedKey]
  );
  const runFileKey = useMemo(
    () => [...new Set((runs || []).map((r) => String(r.sourceFileInfoId)).filter(Boolean))].join(","),
    [runs]
  );

  const sourcesRef = useRef([]);
  sourcesRef.current = sources;

  useEffect(() => {
    if (!question) return undefined;
    // Seed once per (question, persisted set, runs' file set). Re-seeding on every runs refetch would
    // discard an unsaved add.
    const seedKey = `${questionId}|${persistedKey}|${runFileKey}`;
    if (seededForRef.current === seedKey) return undefined;
    seededForRef.current = seedKey;

    const persisted = persistedKey ? persistedKey.split(",") : [];
    const fromRuns = runFileKey ? runFileKey.split(",") : [];
    const wanted = [...new Set([...persisted, ...fromRuns])];

    /*
      Nothing to fetch when the server has simply caught up with what is already on screen — which is the
      common case, because every save invalidates the answer query and so changes `persistedKey`.

      Without this, adding a second file while the first one's save was still in flight made that save's
      refetch reseed to the server's then-current list and drop the second file from the card until its own
      save landed. It came back, but it visibly flickered out, and only the LAST save's list was ever correct.
    */
    const onScreen = idsOf(sourcesRef.current);
    const sameSet =
      onScreen.length === wanted.length && [...onScreen].sort().join() === [...wanted].sort().join();
    if (sameSet) {
      baselineRef.current = onScreen;
      return undefined;
    }

    if (wanted.length === 0) {
      setSources([]);
      baselineRef.current = [];
      return undefined;
    }

    let cancelled = false;
    /*
      allSettled, not all: a source archived since it was attached must cost that one row, not blank the whole
      card. The unresolvable entry is dropped rather than rendered as a broken chip.
    */
    Promise.allSettled(wanted.map((id) => fileinfoService.get(id))).then((results) => {
      if (cancelled) return;
      const loaded = normalizeFileInfoList(
        results.filter((r) => r.status === "fulfilled" && r.value).map((r) => r.value)
      ) || [];
      setSources(loaded);
      baselineRef.current = idsOf(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [question, questionId, persistedKey, runFileKey]);

  const savedAnswer = question?.answer ?? "";
  const answerHtml = answerDraft ?? savedAnswer;
  const answerModified = answerDraft != null && answerDraft !== savedAnswer;
  const sourcesModified = idsOf(sources).join(",") !== baselineRef.current.join(",");
  const answerModifiedFlag = answerModified || sourcesModified;

  const { mutate: saveAnswer, isLoading: isSaving } = useSaveAnswer({
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSuccess: () => {
      setAnswerDraft(null);
      baselineRef.current = idsOf(sources);
      queryClient.invalidateQueries(issbKeys.answer());
      /*
        The year list too, not just the answer: saveAnswer calls updateStageOnEditAnswer, which advances a
        PENDING-or-earlier question to IN_PROGRESS — and the year rows carry a denormalised stage.
      */
      queryClient.invalidateQueries(foundationV2Keys.years());
    },
  });

  /** Save both fields. Always both — see the class comment. */
  const handleSave = useCallback(() => {
    saveAnswer({ questionId, answer: answerHtml, files: idsOf(sources) });
  }, [saveAnswer, questionId, answerHtml, sources]);

  /**
   * Persists a change to the source list immediately, carrying the last SAVED notes rather than the editor's
   * current text.
   *
   * Two reasons. An extraction run must not point at a source that is not persisted; and Save stays honest
   * about the rich text instead of quietly committing prose somebody was still writing.
   */
  const persistSources = useCallback(
    (nextSources) => {
      setSources(nextSources);
      saveAnswer({ questionId, answer: savedAnswer, files: idsOf(nextSources) });
    },
    [saveAnswer, questionId, savedAnswer]
  );

  return {
    sources,
    persistedSourceIds,
    persistSources,
    answerHtml,
    setAnswerDraft,
    answerModifiedFlag,
    handleSave,
    isSaving,
  };
}
